import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { usePageMeta, ORG_JSON_LD } from "../lib/usePageMeta";
import { CAREER_RESOURCES } from "../lib/career-resources";
import { BookOpen, Clock, Sparkles } from "lucide-react";

const META = {
  seo: {
    title: "Career Resources · UK CV & Interview Guides · CV Folks",
    description:
      "Free UK career guides from experienced CV writers: how to write a CV, pass ATS, write cover letters, optimise LinkedIn. Updated 2026.",
    keywords: "cv guide uk, uk career tips, ats guide, cover letter guide",
  },
  jsonLd: ORG_JSON_LD,
};

export default function CareerResourcesHub() {
  usePageMeta(META);
  return (
    <>
      <Navbar />
      <main data-testid="career-resources-hub" className="pt-24 md:pt-32 bg-background pb-24">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <p className="label-eyebrow">Career Resources</p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold mt-3 leading-[1.05] max-w-3xl">
            Free UK career guides from our writers.
          </h1>
          <p className="text-base md:text-lg text-foreground/70 mt-5 max-w-2xl">
            The same principles our UK writers use on every CV — distilled into
            short reads. No fluff, no gated PDFs, no email sign-up.
          </p>

          <div className="mt-14 grid md:grid-cols-2 gap-6">
            {CAREER_RESOURCES.map((r) => (
              <Link
                key={r.slug}
                to={`/career-resources/${r.slug}`}
                data-testid={`resource-card-${r.slug}`}
                className="group rounded-3xl border border-border bg-card p-8 hover:border-brand transition"
              >
                <div className="flex items-start gap-3 text-brand mb-3">
                  <BookOpen className="h-5 w-5" />
                  <div className="flex gap-2 text-[11px] font-semibold uppercase tracking-widest">
                    {r.tags.map((t) => (
                      <span key={t} className="text-foreground/60">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-bold group-hover:text-brand transition">
                  {r.title}
                </h2>
                <p className="text-sm text-foreground/70 mt-3 leading-relaxed">
                  {r.excerpt}
                </p>
                <div className="mt-5 flex items-center justify-between text-xs text-foreground/60">
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" />
                    {r.read_min} min read
                  </span>
                  <span className="text-brand font-semibold">Read →</span>
                </div>
              </Link>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-16 rounded-3xl border border-brand/25 bg-brand/5 p-8 md:p-10 text-center">
            <Sparkles className="h-8 w-8 text-brand mx-auto" />
            <h2 className="font-serif text-2xl md:text-3xl font-bold mt-3">
              Prefer we do the writing?
            </h2>
            <p className="text-base text-foreground/80 mt-3 max-w-2xl mx-auto">
              Start with the £9.99 AI CV Builder or hire a UK writer from £50.
            </p>
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <Link
                to="/ai-cv-maker-builder"
                className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-6 py-3 text-sm font-semibold hover:opacity-90"
              >
                Try AI CV Builder — £9.99
              </Link>
              <Link
                to="/services/cv-writing"
                className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-semibold hover:border-brand hover:text-brand"
              >
                Hire a UK writer
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
